import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

// Weixin JS SDK dynamic loader and console helpers
const WEIXIN_SDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.3.2.js#wechat_redirect';
const ensureWeixinSdk = (() => {
  let promise;
  return () => {
    if (window.wx) return Promise.resolve(window.wx);
    if (promise) return promise;
    promise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = WEIXIN_SDK_URL;
      s.async = true;
      s.onload = () => resolve(window.wx);
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return promise;
  };
})();
// Expose to console
window.ensureWeixinSdk = ensureWeixinSdk;

function inMiniProgram() {
  return new Promise(resolve => {
    const mp = window.wx?.miniProgram;
    if (!mp?.getEnv) return resolve(false);
    try { mp.getEnv(res => resolve(!!res.miniprogram)); } catch (_) { resolve(true); }
  });
}
window.inMiniProgram = inMiniProgram;

async function navigateInMiniProgram(url) {
  try {
    await ensureWeixinSdk();
    const mp = window.wx?.miniProgram;
    if (mp?.getEnv) {
      mp.getEnv(res => {
        if (res?.miniprogram && typeof mp.navigateTo === 'function') {
          mp.navigateTo({ url });
        } else {
          console.log('[WeixinSDK] Not in Mini Program web-view or API unavailable:', url);
        }
      });
    } else if (typeof mp?.navigateTo === 'function') {
      mp.navigateTo({ url });
    } else {
      console.log('[WeixinSDK] miniProgram API not available.');
    }
  } catch (e) {
    console.warn('[WeixinSDK] Failed to load SDK:', e);
  }
}
window.navigateInMiniProgram = navigateInMiniProgram;

// Preload SDK (non-blocking)
ensureWeixinSdk().catch(e => console.warn('[WeixinSDK] preload failed:', e));

// Conditional Eruda (npm) loader: enable with ?eruda=1 ONLY; ignore localStorage.
// Respect sessionStorage 'app.debug.eruda.disabled' to suppress even when URL has eruda=1.
function shouldEnableEruda() {
  try {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('eruda');
    const onByParam = flag !== null && String(flag).toLowerCase() !== '0' && String(flag).toLowerCase() !== 'false';
    let disabled = false;
    try {
      const v = window.sessionStorage.getItem('app.debug.eruda.disabled');
      disabled = !!(v && String(v).toLowerCase() !== '0' && String(v).toLowerCase() !== 'false');
    } catch (_) {}
    return onByParam && !disabled;
  } catch (_) {
    return false;
  }
}

async function initErudaIfEnabled() {
  if (!shouldEnableEruda()) return;
  try {
    const mod = await import('eruda');
    const eruda = mod.default || mod;
    if (eruda && typeof eruda.init === 'function') {
      try {
        eruda.init();
        try { if (typeof eruda.hide === 'function') eruda.hide(); } catch (_) {}
        try {
          if (eruda.get) {
            const entry = eruda.get('entryButton');
            if (entry && typeof entry.show === 'function') entry.show();
          }
        } catch (_) {}
        console.log('[Eruda] initialized (panel hidden, entry shown)');
        // Strip ?eruda param so it won't auto-enable on reload
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.has('eruda')) {
            url.searchParams.delete('eruda');
            window.history.replaceState({}, document.title, url.toString());
          }
        } catch (_) {}
      } catch (e) { console.warn('[Eruda] init error:', e); }
    }
  } catch (e) {
    console.warn('[Eruda] failed to load via dynamic import. Is it installed?', e);
  }
}

// Fire and forget; doesn't block render
initErudaIfEnabled();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
